/* eslint-disable restrict-imports/restrict-imports */
import React, { useState } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, TextInput, Button, Checkbox } from 'design-system';
import { diffWords } from 'diff';
import { Autocomplete } from '@material-ui/lab';
import Slider from '@mui/material/Slider';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import {
  FixedComponentProps,
  EditableCellProps,
  RatingComponentProps,
  RatingEditCellProps,
} from './tbsaTypes';
import { AuditResult } from '@scale/shared/defaults';
import { resultColorsV2, resultIcons } from '../types';
import _ from 'lodash';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { getCellClassName } from './tbsaUtils';
import Linkify from 'react-linkify';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import cs from 'classnames';

// components that don't allow user editing
export const FixedColumnWrapComponent: React.FC<FixedComponentProps> = ({ params, styles }) => {
  const apiRef = useGridApiContext();
  const value = params.value;
  let isJson = false;
  try {
    JSON.stringify(params.value);
    isJson = true;
  } catch {
    // No need to do anything
  }

  return isJson ? (
    <div className={getCellClassName(params, apiRef)}>
      <ReactJsonView
        src={value}
        name={false}
        theme="monokai"
        iconStyle="triangle"
        collapsed={1}
        collapseStringsAfterLength={120}
        groupArraysAfterLength={false}
        enableClipboard={true}
        displayObjectSize={false}
        displayDataTypes={false}
      />
    </div>
  ) : (
    <div className={getCellClassName(params, apiRef)}>
      <div className={styles.textWrap}>
        <div className={styles.textWrapItem}>
          <Linkify>{value}</Linkify>
        </div>
      </div>
    </div>
  );
};

export const TextWrapComponent: React.FC<FixedComponentProps> = ({ params, styles }) => {
  const apiRef = useGridApiContext();
  let value = _.isArray(params.value) && params.value.length > 0 ? params.value[0] : params.value;
  if (_.isNil(value)) {
    value = '';
  }
  return (
    <div className={getCellClassName(params, apiRef)}>
      <div className={styles.textWrap}>
        <div className={styles.textWrapItem}>
          <SanitizedMarkdown text={value} escapeHtml={true} />
        </div>
      </div>
    </div>
  );
};

export const MarkdownWrapComponent: React.FC<FixedComponentProps> = ({ params, styles }) => {
  const apiRef = useGridApiContext();
  return (
    <div className={getCellClassName(params, apiRef)}>
      <div className={cs(styles.textWrap, styles.resetWhiteSpace)}>
        <div className={styles.markdownWrapItem}>
          <SanitizedMarkdown text={params.value ?? ''} />
        </div>
      </div>
    </div>
  );
};

export const AttempterWrapComponent: React.FC<FixedComponentProps> = ({ params }) => {
  const id = params.value.match(/.*\((.*?)\)/)?.[1] ?? params.value;
  const apiRef = useGridApiContext();
  return (
    <div className={getCellClassName(params, apiRef)}>
      {id.length > 0 ? <SmartLink type={INamedObjectTypes.User} id={id} /> : params.value}
    </div>
  );
};

export const IsEditedChip: React.FC<FixedComponentProps> = ({ params, styles }) => {
  const taskChipProps = params.value
    ? {
        label: 'Edited',
        icon: 'wrench',
        fontColor: 'rgb(25, 118, 210, 1)',
        color: 'rgb(25, 118, 210, 0.25)',
      }
    : {
        label: 'Not Edited',
        icon: 'hourglass-start',
        fontColor: 'rgb(0, 0, 0, 1)',
        color: 'rgb(0, 0, 0, 0.1)',
      };
  return (
    <Chip
      label={<div className={styles.chipFontStyle}>{taskChipProps.label}</div>}
      icon={
        <FontAwesomeIcon
          icon={taskChipProps.icon as IconProp}
          style={{ color: `${taskChipProps.fontColor}`, marginLeft: 8 }}
        />
      }
      style={{
        backgroundColor: `${taskChipProps.color}`,
        color: `${taskChipProps.fontColor}`,
      }}
    />
  );
};

export const TextDiffComponent: React.FC<FixedComponentProps> = ({ params, styles }) => {
  if (!params || !params.value) {
    return <span></span>;
  }

  let isObj = true;
  try {
    JSON.parse(params.value);
    isObj = false;
  } catch {
    // No need to do anything
  }
  const value = isObj ? params.value : JSON.parse(params.value);
  let newText = value.newText;
  let originalText = value.originalText;

  if (_.isNil(originalText)) originalText = '';
  if (_.isNil(newText)) newText = '';
  const changes = diffWords(originalText, newText);
  const diffTexts = changes.map((part, partI) => {
    const backgroundColor = part.added ? '#bfb' : undefined;
    const strikethrough = part.removed ? 'line-through' : undefined;
    const textColor = part.added || part.removed ? 'black' : '#888';
    return (
      <span
        key={`diff_${partI}`}
        style={{
          backgroundColor,
          color: textColor,
          textDecoration: strikethrough,
          textDecorationColor: 'maroon',
        }}
      >
        {part.value}
      </span>
    );
  });

  return (
    <div className={cs(styles.textWrap, styles.diffLineHeight)}>
      <div className={styles.textWrapItem}>{diffTexts}</div>
    </div>
  );
};

export const RatingComponent: React.FC<RatingComponentProps> = ({
  params,
  styles,
  setRatingSelected,
}) => {
  const color = resultColorsV2[params.value as AuditResult] || [
    'rgb(0, 0, 0, 0.1)',
    'rgb(0, 0, 0, 1)',
  ];
  const icon = resultIcons[params.value as AuditResult] || 'hourglass-start';
  return (
    <div
      onFocus={() => {
        setRatingSelected(params.id as string);
      }}
      onBlur={() => {
        setRatingSelected(rating => (rating === params.id ? undefined : rating));
      }}
    >
      <Chip
        label={
          <div className={styles.chipFontStyle}>
            {params.value ? _.capitalize(params.value) : 'No Rating'}
          </div>
        }
        icon={
          <FontAwesomeIcon icon={icon as IconProp} style={{ color: color[1], marginLeft: 8 }} />
        }
        style={{
          backgroundColor: color[0],
          color: color[1],
          border: `1px solid ${color[1]}`,
          padding: '8px',
        }}
      />
    </div>
  );
};

// components that do allow the user to edit values
export const AcceptLLMEdits: React.FC<EditableCellProps> = ({
  params,
  updatePastActions,
  editable,
}) => {
  const apiRef = useGridApiContext();
  if (!params || !params.value) {
    return <span></span>;
  }
  const parsedInfo = JSON.parse(params.value);
  return (
    <Button
      disabled={!editable}
      onClick={() => {
        const val: any = { task_links: params.row.task_links };
        val[parsedInfo.field] = parsedInfo.newText;
        _.set(val, `${parsedInfo.field}_diff`, {
          originalText: params.row[`${parsedInfo.field}_diff`].originalText,
          newText: parsedInfo.newText,
        });
        const orig = [params.row];
        const changes = {
          ...val,
          [`copy_edit_${parsedInfo.field}`]: undefined,
          [`levenshtein_${parsedInfo.field}`]: 0,
          [`diff_${parsedInfo.field}`]: JSON.stringify({
            newText: parsedInfo.newText,
            originalText: parsedInfo.newText,
          }),
          isEdited: parsedInfo.newText !== parsedInfo.originalText,
        };
        apiRef.current.updateRows([changes]);
        updatePastActions(changes, orig);
      }}
    >
      Accept
    </Button>
  );
};

export const NumberSliderComponent: React.FC<EditableCellProps> = ({
  params,
  updatePastActions,
  editable,
  styles,
  maxRating,
}) => {
  const apiRef = useGridApiContext();
  const handleChange = (value: number) => {
    const change = {};
    _.set(change, params.field, value as number);
    if (editable) {
      updatePastActions([{ task_links: params.id, ...change }], [params.row]);
      apiRef.current.updateRows([{ task_links: params.id, ...change }]);
    }
  };
  return (
    <div className={styles.sliderComponent}>
      <Slider
        className={styles.slider}
        value={params.value}
        min={0}
        max={maxRating}
        step={1}
        marks
        onChange={(e: Event, newVal: number | number[]) => {
          handleChange(newVal as number);
        }}
      />
      <TextInput
        value={params.value}
        onChange={e => {
          handleChange(parseInt(e.target.value));
        }}
        className={styles.sliderNumber}
        inputProps={{
          step: 1,
          min: 0,
          maxRating,
          type: 'number',
        }}
      />
    </div>
  );
};

export const BooleanCheckboxComponent: React.FC<EditableCellProps> = ({
  params,
  updatePastActions,
  editable,
}) => {
  const apiRef = useGridApiContext();
  return (
    <Checkbox
      label={params.field.replace(/_editable$/, '')}
      checked={params.value}
      onChange={e => {
        const change = {};
        _.set(change, params.field, e.target.checked);
        if (editable) {
          updatePastActions([{ task_links: params.id, ...change }], [params.row]);
          apiRef.current.updateRows([{ task_links: params.id, ...change }]);
        }
      }}
    />
  );
};

export const SelectComponent: React.FC<EditableCellProps> = ({
  params,
  updatePastActions,
  editable,
  selectOptions,
  multipleSelect,
  label,
  placeholder,
}) => {
  const apiRef = useGridApiContext();
  return (
    <Autocomplete
      size="small"
      style={{ width: (params.colDef.width ?? 100) - 24 }}
      defaultValue={
        multipleSelect ? (_.isArray(params.value) ? params.value : []) : params.value ?? ''
      }
      options={(selectOptions ?? []).sort()}
      multiple={multipleSelect}
      onChange={(e, dto) => {
        const change = {};
        _.set(change, params.field, dto);
        if (editable) {
          updatePastActions([{ task_links: params.id, ...change }], [params.row]);
          apiRef.current.updateRows([{ task_links: params.id, ...change }]);
        }
      }}
      renderInput={params => (
        <TextInput
          {..._.omit(params, 'size')}
          size="large"
          label={label ?? 'Select option'}
          placeholder={placeholder ?? 'Select'}
        />
      )}
    />
  );
};

export const RatingEditComponent: React.FC<RatingEditCellProps> = ({
  params,
  styles,
  updatePastActions,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const apiRef = useGridApiContext();

  const handleClose = () => {
    setAnchorEl(undefined);
  };
  const handleOpen = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSelect = async (value: AuditResult) => {
    updatePastActions([{ task_links: params.id, rating: value }], [params.row]);
    apiRef.current.setEditCellValue({
      id: params.id,
      field: params.field,
      value,
    });
    apiRef.current.updateRows([{ task_links: params.id, rating: value }]);
    handleClose();
    apiRef.current.setCellMode(params.id, params.field, 'view');
  };

  const options = [
    { value: AuditResult.Terrible, label: 'Terrible' },
    { value: AuditResult.Bad, label: 'Bad' },
    { value: AuditResult.Ok, label: 'Ok' },
    { value: AuditResult.Good, label: 'Good' },
    { value: AuditResult.Perfect, label: 'Perfect' },
  ];

  const color = resultColorsV2[params.value as AuditResult] || [
    'rgb(0, 0, 0, 0.1)',
    'rgb(0, 0, 0, 1)',
  ];
  const icon = resultIcons[params.value as AuditResult] || 'hourglass-start';

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="outlined"
        className={styles.button}
        color="primary"
        style={{
          textTransform: 'none',
          height: '100% !important',
          borderRadius: 0,
        }}
        fullWidth
      >
        <Chip
          label={
            <div className={styles.chipFontStyle}>
              {params.value ? _.capitalize(params.value) : 'No Rating'}
            </div>
          }
          icon={
            <FontAwesomeIcon icon={icon as IconProp} style={{ color: color[1], marginLeft: 8 }} />
          }
          style={{
            backgroundColor: color[0],
            color: color[1],
            border: `1px solid ${color[1]}`,
            padding: '8px',
          }}
        />
        <FontAwesomeIcon icon="chevron-down" style={{ marginLeft: 'auto' }} />
      </Button>
      <Menu
        id={`menu-audit-result`}
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleClose}
        style={{ marginTop: 52 }}
      >
        {options.map((opt: any) => {
          const optColor = resultColorsV2[opt.value as AuditResult] || [
            'rgb(0, 0, 0, 0.1)',
            'rgb(0, 0, 0, 1)',
          ];
          const optIcon = resultIcons[opt.value as AuditResult] || 'hourglass-start';
          return (
            <MenuItem key={opt.value} onClick={() => handleSelect(opt.value)}>
              <Chip
                label={<div className={styles.chipFontStyle}>{opt.label ?? 'No Rating'}</div>}
                icon={
                  <FontAwesomeIcon
                    icon={optIcon as IconProp}
                    style={{ color: optColor[1], marginLeft: 8 }}
                  />
                }
                style={{
                  backgroundColor: optColor[0],
                  color: optColor[1],
                  border: `1px solid ${optColor[1]}`,
                  padding: '8px',
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
